/* landing-exit-popup.js v1.5.0
 * v1.5.0: Optimistic UI — Shopify mode: success state INSTANT göster (kullanıcı 4sn beklemez),
 *         arka planda 4sn delayed native form.submit() page reload yapar (Shopify subscriber
 *         kayıt için). Kullanıcı kod kopyalama vakti var, reload sonrası popup sessionStorage
 *         flag ile yine success state'inde açılır. AJAX OK ise reload bile yapılmaz.
 * v1.4.1: Shopify mode hibrit — önce AJAX submit dener (token Liquid form'a embed olduysa
 *         200/302 → page reload OLMAZ, anında success state, ~300ms UX). Başarısız olursa
 *         native form.submit() ile page reload fallback (eski 4sn akışı, %100 garantili).
 *         Best-case: hızlı UX. Worst-case: eski davranış.
 *
 * v1.4.0: Popup içindeki form artık Liquid {% form 'customer' %} — Shopify reCAPTCHA invisible
 *         widget visible form'da otomatik çalışır → token enjekte edilir → backend kabul eder.
 *         Shopify mode'da: native page-reload submit (e.preventDefault YOK), sessionStorage flag
 *         set edilir → reload sonrası popup success state'inde otomatik açılır.
 *         Webhook/none mode'lar AJAX submit ile devam (preventDefault var).
 *         AJAX fetch + offscreen form + native fallback logic kaldırıldı (artık gereksiz).
 * v1.3.0: Robust submit — AJAX dene, başarısızsa (4xx/5xx/network error) gizli formu
 *         native `.submit()` ile page-reload ederek Shopify standart path'ından geçir.
 *         Native submit her zaman çalışır (CSRF token Liquid form'da gömülü, accepts_marketing var).
 *         Sayfa redirect olur ama subscriber kaydı garantilenir.
 *         Discount code success state cookie'ye yazılır → reload sonrası popup tekrar açılıp success gösterir.
 * v1.2.0: Shopify submit fix — manuel FormData yerine gizli Liquid {% form 'customer' %}
 *         kullanılıyor. authenticity_token (CSRF) + form_type + utf8 hepsi Liquid tarafından
 *         otomatik basılır → POST /contact backend tarafından kabul edilir → subscriber kaydı oluşur.
 *         data-shopify-form-id attribute ile section'daki hidden form'a bağlanır.
 * v1.1.0: Copy-code button (clipboard API + label swap + .is-copied class, 2s reset).
 * v1.0.0: Initial — trigger + cooldown + form submit.
 * Custom element: <landing-exit-popup>
 * Triggers (data attributes):
 *   data-trigger-mouse  : "true"/"false" — mouseleave to top (desktop)
 *   data-trigger-scroll : 0-100 (%) — scroll up after this depth
 *   data-min-time       : seconds — min time on page before trigger
 *   data-visibility     : "all" | "desktop" | "mobile"
 *   data-cooldown       : "session" | "days" | "always"
 *   data-cooldown-days  : N
 *   data-storage-key    : localStorage key
 *   data-integration    : "shopify" | "webhook" | "none"
 *   data-webhook-url    : URL
 *   data-customer-tag   : string
 */
class LandingExitPopup extends HTMLElement {
  connectedCallback() {
    this.openedAt = Date.now();
    this.opened = false;
    this.maxScroll = 0;
    this.lastScrollY = window.scrollY;

    this.triggerMouse  = this.dataset.triggerMouse === 'true';
    this.triggerScroll = parseFloat(this.dataset.triggerScroll) || 0;
    this.minTime       = (parseFloat(this.dataset.minTime) || 0) * 1000;
    this.visibility    = this.dataset.visibility || 'all';
    this.cooldown      = this.dataset.cooldown || 'session';
    this.cooldownDays  = parseFloat(this.dataset.cooldownDays) || 7;
    this.storageKey    = this.dataset.storageKey || 'landing-exit-popup-seen';
    this.integration   = this.dataset.integration || 'none';
    this.webhookUrl    = this.dataset.webhookUrl || '';
    this.customerTag   = this.dataset.customerTag || 'exit-intent-popup';

    // Bindings setup (close/copy needed even in fallback success display).
    this._bindClose();
    this._bindForm();
    this._bindCopy();

    // If returning from native form fallback submit → show success state immediately.
    try {
      if (sessionStorage.getItem('landing-exit-popup-submitted') === '1') {
        sessionStorage.removeItem('landing-exit-popup-submitted');
        this._open();
        this._showSuccess();
        this._markSeen();
        return;
      }
    } catch (e) {}

    if (this._isOnCooldown()) return;
    if (!this._visibilityMatches()) return;

    this._bindTriggers();
  }

  disconnectedCallback() {
    if (this._mouseHandler)  document.removeEventListener('mouseout', this._mouseHandler);
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
  }

  _isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  _visibilityMatches() {
    if (this.visibility === 'all') return true;
    if (this.visibility === 'desktop' && !this._isMobile()) return true;
    if (this.visibility === 'mobile'  &&  this._isMobile()) return true;
    return false;
  }

  _isOnCooldown() {
    if (this.cooldown === 'always') return false;

    try {
      if (this.cooldown === 'session') {
        return sessionStorage.getItem(this.storageKey) === '1';
      }
      if (this.cooldown === 'days') {
        var raw = localStorage.getItem(this.storageKey);
        if (!raw) return false;
        var seenAt = parseInt(raw, 10);
        if (isNaN(seenAt)) return false;
        var elapsedDays = (Date.now() - seenAt) / 86400000;
        return elapsedDays < this.cooldownDays;
      }
    } catch (e) {
      return false;
    }
    return false;
  }

  _markSeen() {
    try {
      if (this.cooldown === 'session') sessionStorage.setItem(this.storageKey, '1');
      else if (this.cooldown === 'days') localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (e) {}
  }

  _bindTriggers() {
    var self = this;

    if (this.triggerMouse && !this._isMobile()) {
      this._mouseHandler = function(e) {
        if (self.opened) return;
        if (Date.now() - self.openedAt < self.minTime) return;
        if (e.clientY < 20 && !e.relatedTarget && !e.toElement) {
          self._open();
        }
      };
      document.addEventListener('mouseout', this._mouseHandler);
    }

    if (this.triggerScroll > 0 || this._isMobile()) {
      this._scrollHandler = function() {
        if (self.opened) return;
        if (Date.now() - self.openedAt < self.minTime) return;

        var y = window.scrollY;
        var docHeight = document.documentElement.scrollHeight - window.innerHeight;
        var pct = docHeight > 0 ? (y / docHeight) * 100 : 0;
        if (pct > self.maxScroll) self.maxScroll = pct;

        var scrollingUp = y < self.lastScrollY - 5;
        self.lastScrollY = y;

        if (self.maxScroll >= self.triggerScroll && scrollingUp && self.maxScroll > 20) {
          self._open();
        }
      };
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }
  }

  _open() {
    if (this.opened) return;
    this.opened = true;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    var focusable = this.querySelector('input, button, textarea');
    if (focusable) setTimeout(function() { focusable.focus(); }, 50);
  }

  _close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    this._markSeen();
  }

  _bindClose() {
    var self = this;
    this.querySelectorAll('[data-lep-close]').forEach(function(el) {
      el.addEventListener('click', function() { self._close(); });
    });
    this.addEventListener('click', function(e) {
      if (e.target === self) self._close();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.opened) self._close();
    });
  }

  _bindForm() {
    var self = this;
    var form = this.querySelector('form.landing-exit-popup__form');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      var emailInput = form.querySelector('input[type="email"]');
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) { e.preventDefault(); return; }

      var submitBtn = form.querySelector('[type="submit"]');

      // Shopify mode: optimistic UI + hybrid AJAX/native.
      // Best case (AJAX OK): instant success state, no reload.
      // Fallback (hCaptcha rejects AJAX): success state shown INSTANTLY (optimistic),
      // background native form.submit() fires after 4s delay (gives user time to copy code,
      // then page reloads and popup re-opens in success state via sessionStorage flag).
      if (self.integration === 'shopify') {
        e.preventDefault();
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }
        var scheduleNativeFallback = function() {
          try { sessionStorage.setItem('landing-exit-popup-submitted', '1'); } catch (err) {}
          // 4s delay: user copies code, then background reload commits the subscription.
          setTimeout(function() { form.submit(); }, 4000);
        };
        var fd = new FormData(form);
        fetch(form.action, {
          method: 'POST',
          body: fd,
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        }).then(function(response) {
          if (response.ok || response.status === 302) {
            // AJAX succeeded — subscription committed, no reload needed.
            self._showSuccess();
            self._markSeen();
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.classList.remove('is-loading');
            }
          } else {
            // 4xx/5xx (usually hCaptcha): optimistic success NOW + delayed reload commits subscription.
            self._showSuccess();
            self._markSeen();
            scheduleNativeFallback();
          }
        }).catch(function(err) {
          console.warn('[landing-exit-popup] AJAX failed, optimistic + native fallback', err);
          self._showSuccess();
          self._markSeen();
          scheduleNativeFallback();
        });
        return;
      }

      // Webhook / none / unknown mode: AJAX submit + success state göster.
      e.preventDefault();
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }
      self._submit(email).then(function() {
        self._showSuccess();
      }).catch(function(err) {
        console.warn('[landing-exit-popup] submit failed', err);
        self._showSuccess();
      }).finally(function() {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
        self._markSeen();
      });
    });
  }

  _submit(email) {
    // Shopify mode is handled in _bindForm() via native submit (reCAPTCHA path).
    // This method only handles webhook / none integrations.
    if (this.integration === 'webhook' && this.webhookUrl) {
      return fetch(this.webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          tag: this.customerTag,
          source: 'landing-exit-popup',
          page: window.location.href,
          ts: new Date().toISOString()
        })
      });
    }

    return Promise.resolve();
  }

  _showSuccess() {
    var formWrap = this.querySelector('[data-lep-form-wrap]');
    var success  = this.querySelector('[data-lep-success]');
    if (formWrap) formWrap.style.display = 'none';
    if (success)  success.style.display  = 'block';
  }

  _bindCopy() {
    var btn = this.querySelector('[data-lep-copy]');
    if (!btn) return;

    var self = this;
    btn.addEventListener('click', function() {
      var codeEl = self.querySelector('[data-lep-code-value]');
      var code = codeEl ? codeEl.textContent.trim() : '';
      if (!code) return;

      var done = function() {
        var textEl = btn.querySelector('[data-lep-copy-text]');
        var defaultLabel = btn.dataset.lepCopyDefault || 'Copy';
        var doneLabel    = btn.dataset.lepCopyDone    || 'Copied!';
        btn.classList.add('is-copied');
        if (textEl) textEl.textContent = doneLabel;
        setTimeout(function() {
          btn.classList.remove('is-copied');
          if (textEl) textEl.textContent = defaultLabel;
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(function() {
          self._copyFallback(code, done);
        });
      } else {
        self._copyFallback(code, done);
      }
    });
  }

  _copyFallback(text, onDone) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
    if (onDone) onDone();
  }
}

if (!customElements.get('landing-exit-popup')) {
  customElements.define('landing-exit-popup', LandingExitPopup);
}
